import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PulseLoader } from 'react-spinners';
import { SectionWrapper } from 'resources/styles/CommonStyles';
import { Button, Row } from 'saladsoft-primitive';
import { ProductDetailDto } from 'services/data-contracts';
import ServiceHelper from 'stores/ServiceHelper';
import styled from 'styled-components';

const CrawlerSection = ({ url }: { url?: string | null }) => {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [wrapperHeight, setWrapperHeight] = useState<number>(0);
  const prevHeightRef = useRef<number>(0);

  const [product, setProduct] = useState<ProductDetailDto | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const handleClickSite = () => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleCrawl = () => {
    setLoading(true);
    if (!url) {
      return;
    }
    ServiceHelper.crawlerService?.getProductDetails({ 'target-url': url }).then((res) => {
      if (res.status === 200) {
        setProduct(res.data);
        setHasError(false);
      } else {
        const error = (res as any).response?.data?.errorMessage;
        setHasError(error);
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    if (url) {
      handleCrawl();
    } else {
      // This domain is not supported for crawling. 이나 Product가 존재하는 화면에서 url이 존재하지 않는 화면으로
      // 이동시에 상품이미지와 This domain is not supported for crawling. 메시지가 사라지지 않는 문제 수정
      setProduct(null);
      setHasError(false);
    }
  }, [url]);

  useEffect(() => {
    return () => {
      setProduct(null);
      setHasError(false);
    };
  }, []);

  useEffect(() => {
    if (!wrapperRef.current) return;

    let resizeTimeout: NodeJS.Timeout | null = null;

    // 모달 요소 찾기 및 높이 계산 함수
    const calculateHeight = () => {
      if (!wrapperRef.current) return;

      // ReactModal 요소 찾기
      const modalElement = document.querySelector(
        '.ReactModal__Content.ReactModal__Content--after-open',
      );

      if (modalElement) {
        // 모달의 높이 가져오기
        const modalHeight = modalElement.getBoundingClientRect().height;

        // 컨텐츠에 맞는 높이 계산 (모달 높이에서 여백 등 고려)
        const contentHeight = modalHeight - 100; // 상하 여백 고려하여 조정

        prevHeightRef.current = contentHeight;
        setWrapperHeight(contentHeight);
      } else {
        // 모달이 없는 경우 기존 방식으로 계산
        wrapperRef.current.style.height = 'auto';
        const contentHeight = wrapperRef.current.getBoundingClientRect().height;
        prevHeightRef.current = contentHeight;
        setWrapperHeight(contentHeight);
      }
    };

    // 초기 높이 설정 및 product/loading 상태 변경 시 높이 재계산
    const updateHeight = () => {
      // 약간의 지연을 두어 DOM이 완전히 렌더링된 후 높이 계산
      setTimeout(calculateHeight, 100);
    };

    // 초기 실행
    updateHeight();

    // ResizeObserver 설정
    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }

      resizeTimeout = setTimeout(updateHeight, 100);
    });

    resizeObserver.observe(wrapperRef.current);

    // 모달 요소도 관찰
    const modalElement = document.querySelector(
      '.ReactModal__Content.ReactModal__Content--after-open',
    );
    if (modalElement) {
      resizeObserver.observe(modalElement as Element);
    }

    return () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeObserver.disconnect();
    };
  }, [product, loading]); // product와 loading 상태가 변경될 때마다 실행

  return (
    <ContainerWrpper
      ref={wrapperRef}
      style={{ height: wrapperHeight > 0 ? `calc(${wrapperHeight}px - 100px)` : 'auto' }}
      className="crawler-section-wrapper1"
    >
      <Row
        justifyContent="space-between"
        alignItems="center"
        gap={10}
        marginBottom={'20px !important'}
      >
        {/* <>{getLogo()}</> */}
        <>
          {url ? (
            <Button
              isValid={url !== ''}
              type="outlined"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                handleClickSite();
              }}
            >
              {t('visit site')}
            </Button>
          ) : (
            <div>{t('no-url')}</div>
          )}
        </>
      </Row>

      {loading ? (
        <LoadingWrapper style={{ height: `${wrapperHeight}px` }}>
          <PulseLoader />
        </LoadingWrapper>
      ) : (
        <>
          <>{hasError && <div style={{ margin: '100px 0' }}>{hasError}</div>}</>
          {product && !hasError && (
            <Row justifyContent="flex-start" alignItems="flex-start" gap={30}>
              <ImageWrapper>
                <img
                  src={
                    product.imageUrls && product.imageUrls.length > 0
                      ? product.imageUrls[0]
                      : '/images/no-photo.webp'
                  }
                  alt="product"
                  style={{ width: '100%', maxWidth: 300 }}
                />
              </ImageWrapper>
              <div>
                <SectionWrapper>
                  <div>{t('Product Name')}</div>
                  <div>{product.productName || t('no-title')}</div>
                </SectionWrapper>
                <SectionWrapper>
                  <div>{t('Product Price')}</div>
                  <div>{product.productPrice || t('no-price')}</div>
                </SectionWrapper>
                <SectionWrapper>
                  <div>{t('options')}</div>
                  {product.options && product.options?.length > 0 ? (
                    <ul>
                      {product.options?.map((option) => (
                        <li key={option.optionName}>{option.optionName}</li>
                      ))}
                    </ul>
                  ) : (
                    <div>{t('No options')}</div>
                  )}
                </SectionWrapper>
              </div>
            </Row>
          )}
        </>
      )}
    </ContainerWrpper>
  );
};

const ContainerWrpper = styled.div`
  padding: 20px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  border-radius: 20px;
  margin-top: 20px;
  overflow: auto;
`;

const LoadingWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageWrapper = styled.div`
  width: 300px;
  height: 300px;
  overflow: hidden;

  border-radius: 10px;
  margin: 10px 0;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: bold;
  margin: 10px 0;
  white-space: pre-wrap;
`;

const Subject = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin: 10px 0;
  color: #001f69;
`;

export default CrawlerSection;
